// Task card component for supplier dashboard

import React from 'react'
import { CountdownTimer } from './CountdownTimer'
import styles from './TaskCard.module.css'

export interface SupplierTask {
  id: string
  title: string
  description?: string
  startTime: string // ISO date string
  endTime: string
  duration?: number // in minutes
  location?: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  acknowledged: boolean
  acknowledgedAt?: string
  acknowledgedBy?: string
  notes?: string
  requirements?: string[]
  contacts?: {
    name: string
    phone?: string
    role?: string
  }[]
}

interface TaskCardProps {
  task: SupplierTask
  onAcknowledge: () => void
  showCountdown?: boolean
  variant?: 'default' | 'compact'
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onAcknowledge,
  showCountdown = false,
  variant = 'default'
}) => {
  const startTime = new Date(task.startTime)
  const endTime = new Date(task.endTime)
  const now = new Date()
  const isPast = startTime <= now
  const isUpcoming = startTime > now && !task.acknowledged
  const isUrgent = task.priority === 'urgent'
  const isHigh = task.priority === 'high'

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatDate = (date: Date) => {
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow'
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    }
  }

  const getPriorityColor = () => {
    switch (task.priority) {
      case 'urgent':
        return '#dc2626'
      case 'high':
        return '#ea580c'
      case 'medium':
        return '#d97706'
      default:
        return '#059669'
    }
  }

  const getStatusIcon = () => {
    if (task.acknowledged) {
      return '✅'
    } else if (isUpcoming) {
      return '⏰'
    } else if (isPast && !task.acknowledged) {
      return '⚠️'
    }
    return '📋'
  }

  const cardClassName = `
    ${styles.card} 
    ${variant === 'compact' ? styles.compact : ''}
    ${task.acknowledged ? styles.acknowledged : ''}
    ${isUpcoming ? styles.upcoming : ''}
    ${isPast && !task.acknowledged ? styles.overdue : ''}
    ${isUrgent ? styles.urgent : ''}
  `.trim()

  return (
    <div className={cardClassName}>
      {/* Priority indicator */}
      <div 
        className={styles.priorityBar}
        style={{ backgroundColor: getPriorityColor() }}
      />

      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.timeInfo}>
            <div className={styles.timeSlot}>
              <span className={styles.date}>{formatDate(startTime)}</span>
              <span className={styles.time}>
                {formatTime(startTime)} - {formatTime(endTime)}
              </span>
              {task.duration && (
                <span className={styles.duration}>({task.duration}min)</span>
              )}
            </div>
          </div>

          <div className={styles.statusIcon} title={task.acknowledged ? 'Acknowledged' : 'Pending'}>
            {getStatusIcon()}
          </div>
        </div>

        {/* Task details */}
        <div className={styles.body}>
          <h3 className={styles.title}>{task.title}</h3>
          
          {task.description && (
            <p className={styles.description}>{task.description}</p>
          )}

          {task.location && (
            <div className={styles.location}>
              <span className={styles.locationIcon}>📍</span>
              {task.location}
            </div>
          )}

          {task.requirements && task.requirements.length > 0 && (
            <div className={styles.requirements}>
              <strong>Requirements:</strong>
              <ul>
                {task.requirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </div>
          )}

          {task.contacts && task.contacts.length > 0 && (
            <div className={styles.contacts}>
              <strong>Contacts:</strong>
              {task.contacts.map((contact, index) => (
                <div key={index} className={styles.contact}>
                  {contact.name}
                  {contact.role && <span className={styles.contactRole}>({contact.role})</span>}
                  {contact.phone && <a href={`tel:${contact.phone}`} className={styles.contactPhone}>{contact.phone}</a>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Countdown timer */}
        {showCountdown && isUpcoming && (
          <div className={styles.countdown}>
            <CountdownTimer
              targetTime={task.startTime}
              variant="inline"
              showLabel={false}
            />
          </div>
        )}

        {/* Acknowledgment section */}
        <div className={styles.footer}>
          {task.acknowledged ? (
            <div className={styles.acknowledgedInfo}>
              <div className={styles.acknowledgedBadge}>
                ✓ Acknowledged
              </div>
              {task.acknowledgedAt && (
                <div className={styles.acknowledgedTime}>
                  {new Date(task.acknowledgedAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </div>
              )}
              {task.notes && (
                <div className={styles.acknowledgedNotes}>
                  <strong>Notes:</strong> {task.notes}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.actions}>
              {isPast ? (
                <div className={styles.overdueWarning}>
                  ⚠️ This task has started. Please acknowledge or contact coordination.
                </div>
              ) : (
                <button
                  className={styles.acknowledgeButton}
                  onClick={onAcknowledge}
                >
                  Acknowledge Task
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Urgent indicator */}
      {isUrgent && (
        <div className={styles.urgentIndicator}>
          🚨 URGENT
        </div>
      )}
    </div>
  )
}